import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doAseSetGroupFilter } from 'store/actions';
import { formatDate } from 'lib/riskbase';
import {
  selectAseTplFieldGroup,
  selectAseTplFieldInstanceChecksForGroup,
  selectAseFieldGroupFilter,
  selectAseIsEditable,
} from 'store/assessments/selectors';
import type { AvailabilityCategoryText } from 'store/assessments/types';
import { FieldRepeatableOrderEnum } from 'types/types';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { OptionField } from 'components/Field/OptionField';
import { produce } from 'immer';
import { CardRow } from 'components/CardRow';
import {
  getRepeatableFilterKey,
  getRepeatableFilterOrderCheckValue,
} from 'store/assessments/utilities';

import { ExplorerOptions } from '../Explorer/ExplorerOptions';
import { UI_DATE_FORMAT_SHORT_WITHOUT_YEAR } from 'lib/constants';
import { addDays } from 'date-fns';
import { PageHeader } from 'components/PageHeader';
import { GapSmall } from 'components/GapSmall';
import { ViewWithSeparators } from 'components/ViewWithSeparators';

export const RepeatableOptionsModal: React.FC<
  ModalScreenProps<ModalScreens.AseFieldInstancesOptions>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { groupId, listType } = route.params;

  const groupFilterKey = getRepeatableFilterKey(groupId, listType);
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const groupFilter = useSelector((state) =>
    selectAseFieldGroupFilter(state, groupFilterKey),
  );
  const checks = useSelector((state) =>
    selectAseTplFieldInstanceChecksForGroup(state, groupId),
  );
  const isEditable = useSelector(selectAseIsEditable);
  const selectedAvailabilityCategories = Object.values(
    groupFilter.availabilityCategories,
  );

  const handleSetOrder = (newOrderValue: FieldRepeatableOrderEnum) =>
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          draft.order = newOrderValue;
        }),
      }),
    );
  const today = new Date();

  const handleSetMissingValues = (newValue: boolean) =>
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          draft.requiredFields = newValue;
        }),
      }),
    );

  const handleSetCheck = (
    checkId: string,
    value: 'required' | 'notRequired' | null,
  ) =>
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          if (!value) {
            delete draft.checks[checkId];
          } else {
            draft.checks[checkId] = value;
          }
        }),
      }),
    );

  const handleSetAvailability = (
    availabilityCategory: AvailabilityCategoryText,
  ) => {
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          const selected = draft.availabilityCategories || [];
          if (selected.includes(availabilityCategory)) {
            draft.availabilityCategories = selected.filter(
              (category) => category !== availabilityCategory,
            );
            return;
          }
          draft.availabilityCategories = [...selected, availabilityCategory];
        }),
      }),
    );
  };
  const isAvailabilitySelected = React.useCallback(
    (availabilityCategory: AvailabilityCategoryText) =>
      selectedAvailabilityCategories.includes(availabilityCategory),
    [selectedAvailabilityCategories],
  );
  const handleClose = React.useCallback(
    () => dispatch(NAVIGATE_MODAL_BACK()),
    [dispatch],
  );

  React.useEffect(() => {
    if (!group) {
      handleClose();
    }
  }, [dispatch, group, handleClose]);

  useEsc(handleClose);

  if (!group) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="View Options"
          subtitle={group.name}
          onPressSaveButton={() => dispatch(NAVIGATE_MODAL_BACK())}
        />
      }
    >
      <Card label="Order By">
        <OptionField
          modalSize="small"
          modalLabel={`Order ${group.name} By`}
          value={[groupFilter.order]}
          onChange={(value) =>
            handleSetOrder(value[0] as FieldRepeatableOrderEnum)
          }
          options={[
            {
              name: 'Created',
              value: 'Oldest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.createdAsc,
              groupName: 'Created',
            },
            {
              name: 'Created',
              value: 'Newest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.createdDesc,
              groupName: 'Created',
            },
            {
              name: 'Updated',
              value: 'Oldest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.updatedAsc,
              groupName: 'Updated',
            },
            {
              name: 'Updated',
              value: 'Newest first',
              internalValue: FieldRepeatableOrderEnum.updatedDesc,
              groupName: 'Updated',
              labelAlignTop: true,
            },
            ...Object.values(checks)
              .map((check) => [
                {
                  name: 'Check Last Completed',
                  value: `${check.name} • Oldest first`,
                  internalValue: getRepeatableFilterOrderCheckValue(
                    check.id,
                    'completed',
                    'asc',
                  ),
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
                {
                  name: 'Check Last Completed',
                  value: `${check.name} • Newest first`,
                  internalValue: getRepeatableFilterOrderCheckValue(
                    check.id,
                    'completed',
                    'desc',
                  ),
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
                {
                  name: 'Check Last Attempted',
                  value: `${check.name} • Oldest first`,
                  internalValue: getRepeatableFilterOrderCheckValue(
                    check.id,
                    'attempted',
                    'asc',
                  ),
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
                {
                  name: 'Check Last Attempted',
                  value: `${check.name} • Newest first`,
                  internalValue: getRepeatableFilterOrderCheckValue(
                    check.id,
                    'attempted',
                    'desc',
                  ),
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
              ])
              .flat(),
          ]}
          expandedInlineFirst
          expandedInlineLast
        />
      </Card>
      {isEditable &&
        Object.values(checks).map((check) => (
          <GapSmall key={`check-${check.id}`}>
            <Card label={check.name}>
              <ViewWithSeparators horizontal>
                <CardRow
                  label="All"
                  style={{ width: 100 }}
                  radioValue={!groupFilter.checks[check.id]}
                  onPress={() => handleSetCheck(check.id, null)}
                />
                <CardRow
                  label="Outstanding"
                  style={{ flex: 1 }}
                  radioValue={groupFilter.checks[check.id] === 'required'}
                  onPress={() => handleSetCheck(check.id, 'required')}
                  showBadge={groupFilter.checks[check.id] === 'required'}
                />
                <CardRow
                  label="Completed"
                  style={{ flex: 1 }}
                  radioValue={groupFilter.checks[check.id] === 'notRequired'}
                  onPress={() => handleSetCheck(check.id, 'notRequired')}
                  showBadge={groupFilter.checks[check.id] === 'notRequired'}
                />
              </ViewWithSeparators>
            </Card>
            <Card>
              <CardRow
                label="Resident Available Today"
                labelAlignTop
                onPress={() => handleSetAvailability('Today')}
                checkedValue={isAvailabilitySelected('Today')}
                showBadge={isAvailabilitySelected('Today')}
              />
              <CardRow
                label="Resident Available Tomorrow"
                labelAlignTop
                onPress={() => handleSetAvailability('Tomorrow')}
                checkedValue={isAvailabilitySelected('Tomorrow')}
                showBadge={isAvailabilitySelected('Tomorrow')}
              />
              <CardRow
                label="Resident Available Next 7 Days"
                value={`Until ${formatDate(addDays(today, 7), UI_DATE_FORMAT_SHORT_WITHOUT_YEAR)}`}
                onPress={() => handleSetAvailability('Next 7 Days')}
                checkedValue={isAvailabilitySelected('Next 7 Days')}
                showBadge={isAvailabilitySelected('Next 7 Days')}
              />
            </Card>
          </GapSmall>
        ))}
      {isEditable && listType === 'added' && (
        <Card>
          <CardRow
            label="Incomplete Fields"
            value={`Only show ${group.name.toLowerCase()} where fields are incomplete`}
            labelAlignTop
            onPress={() => handleSetMissingValues(!groupFilter.requiredFields)}
            checkedValue={groupFilter.requiredFields}
            showBadge={groupFilter.requiredFields}
          />
        </Card>
      )}

      {listType === 'added' && (
        <>
          <SeparatorHorizontal />
          <ExplorerOptions />
        </>
      )}
    </Page>
  );
};
